import { RouteChildNodeDataType, RouteNodeType } from '@lp/features/RoutePlanner/types';
import {
  AircraftLegResource,
  BaseLegResourceType,
  SegmentTypeEnum,
  AviaLegTypes,
  GroundLegTypes,
  RouteLocationType,
} from '@/api/apis/route';
import { OrganType } from '@/api/apis/case';
import { RouteSegmentType } from '@/types/route';
import { getSegmentMapKey } from '@/utils/route';
import { WithExtendedLocation } from '@/utils/helpers';
import {
  getBaseChildNode,
  getBaseLocationNodes,
  getBaseParentNode,
  getBaseSegmentNode,
  getParentNodeId,
} from './nodes';
import { getChildEdge, getCurrentEdgeWidth, getDefaultEdgeWidth, getMainEdgeData } from './edges';
import { normalizeRouteData, getSegmentLeg } from './route';
import { computeUnconnectedLegs, TreeLeg } from './treeLayout';
import { calculateNodeRect, getUpdatedChildNode, getUpdatedNode, normalizeNodesPositions } from './schema';

export const getRouteEdges = (
  segments: RouteSegmentType[],
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>,
  parentNodes: RouteNodeType[],
  childNodes: RouteNodeType<RouteChildNodeDataType>[],
  aircrafts: BaseLegResourceType<AircraftLegResource>[],
  schemaType: 'main' | 'alt' = 'main',
): { edges: unknown[]; nodes: (RouteNodeType | RouteNodeType<RouteChildNodeDataType>)[]; blockHeight: number } => {
  const edgeWidth = getDefaultEdgeWidth(parentNodes, schemaType);
  const { mainNodeY, blockHeight } = calculateNodeRect(childNodes);

  const edges: unknown[] = [];
  const updatedChildNodes: RouteNodeType<RouteChildNodeDataType>[] = [];
  const updatedNodes: RouteNodeType[] = [];

  const segmentsById = new Map(segments.map((s) => [s.id, s]));

  const childNodesByParentId = new Map<string | null | undefined, RouteNodeType<RouteChildNodeDataType>[]>();
  childNodes.forEach((c) => {
    const parentChildren = childNodesByParentId.get(c.data.parentId) || [];
    parentChildren.push(c);
    childNodesByParentId.set(c.data.parentId, parentChildren);
  });

  let prevNodeX = 0;

  parentNodes.forEach((node, index) => {
    const children = childNodesByParentId.get(node.id) || [];
    const prevNodeItem = updatedNodes[updatedNodes.length - 1];
    prevNodeX = prevNodeItem ? prevNodeItem.position.x : 0;
    prevNodeX += prevNodeItem?.width || 0;

    const currentNode = getUpdatedNode(node, !index ? 0 : prevNodeX, mainNodeY, schemaType);

    if (children.length) {
      const topChildren = children.filter((c) => c.data.connectionType === 'to');
      const bottomChildren = children.filter((c) => c.data.connectionType === 'from');
      currentNode.data.childrenTop = topChildren.length;
      currentNode.data.childrenBottom = bottomChildren.length;
      currentNode.position.x += getCurrentEdgeWidth(currentNode, prevNodeItem, edgeWidth);

      topChildren.forEach((c, cIndex) => {
        const currentChildSegmentLeg = getSegmentLeg(segmentsById, legs, c);

        const updatedChildNode = getUpdatedChildNode({
          node: c,
          parentNodeX: currentNode.position.x,
          nodeY: 30,
          index: cIndex,
          parentNodeWidth: currentNode.width,
          resource: currentChildSegmentLeg?.resources?.[0]?.resourceType,
        });

        const childEdge = getChildEdge({
          currentNode: c,
          parentNode: currentNode,
          currentLeg: currentChildSegmentLeg,
          aircrafts,
          connectionType: c.data.connectionType!,
          index: cIndex,
        });

        updatedChildNodes.push(updatedChildNode);
        edges.push(childEdge);
      });

      bottomChildren.forEach((c, cIndex) => {
        const currentChildSegmentLeg = getSegmentLeg(segmentsById, legs, c);

        const updatedChildNode = getUpdatedChildNode({
          node: c,
          parentNodeX: currentNode.position.x,
          nodeY: mainNodeY + 55,
          index: cIndex,
          parentNodeWidth: currentNode.width,
          resource: currentChildSegmentLeg?.resources?.[0]?.resourceType,
        });

        const childEdge = getChildEdge({
          currentNode: c,
          parentNode: currentNode,
          currentLeg: currentChildSegmentLeg,
          aircrafts,
          connectionType: c.data.connectionType!,
          index: cIndex,
        });

        updatedChildNodes.push(updatedChildNode);
        edges.push(childEdge);
      });
    }

    if (!index) {
      updatedNodes.push(currentNode);
      return;
    }

    if (!children.length) {
      currentNode.position.x += edgeWidth;
    }

    edges.push(getMainEdgeData({ node, nodes: updatedNodes, segmentsById, legs, aircrafts }));

    updatedNodes.push(currentNode);
  });

  const nodes = normalizeNodesPositions(updatedNodes, updatedChildNodes);

  return { edges, nodes, blockHeight };
};

type GetNodesProps = {
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  locations: WithExtendedLocation<RouteLocationType>[];
  organType: OrganType;
  routeType: 'full' | 'main';
  schemaType: 'main' | 'alt';
};

export const getNodes = ({
  segments: schemaSegments,
  legs: schemaLegs,
  locations,
  organType,
  routeType,
  schemaType,
}: GetNodesProps): {
  parentNodes: RouteNodeType[];
  childNodes: RouteNodeType<RouteChildNodeDataType>[];
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  unconnectedSegments: RouteSegmentType[];
} => {
  const parentNodes: RouteNodeType[] = [];
  const childNodes: RouteNodeType<RouteChildNodeDataType>[] = [];
  const unconnectedSegments: RouteSegmentType[] = [];

  const parentSegments = schemaSegments.filter(
    (seg) => ![SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase, SegmentTypeEnum.custom].includes(seg.type),
  );

  const baseNodes = getBaseLocationNodes({
    segments: parentSegments,
    legs: schemaLegs,
    locations,
  });

  baseNodes.forEach((node) => {
    parentNodes.push(getBaseParentNode(node, organType, schemaType));
  });

  const customSegments = schemaSegments.filter((seg) =>
    [SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase, SegmentTypeEnum.custom].includes(seg.type),
  );

  customSegments
    .filter((s) => !s.isAccommodation)
    .forEach((s) => {
      const parentNodeInfo = getParentNodeId(s, baseNodes);

      if (!parentNodeInfo) {
        unconnectedSegments.push(s);
      }

      if (parentNodeInfo) {
        const segmentLegs = schemaLegs.get(getSegmentMapKey(s)) || [];
        const isFromParent = parentNodeInfo.connectionType === 'from';
        const nodeLocation = isFromParent ? s.endLocation : s.startLocation;
        const baseLocationNode = getBaseSegmentNode(s, nodeLocation, segmentLegs, locations);

        const isReposition = s.type === SegmentTypeEnum.reposition;
        const isReturnToBase = s.type === SegmentTypeEnum.returnToBase;
        const parentNodeIndex = parentNodes.findIndex((p) => p.id === parentNodeInfo.parentId);

        if (isReposition && parentNodeIndex === 0) {
          parentNodes.unshift(getBaseParentNode(baseLocationNode, organType, schemaType));
        } else if (isReturnToBase && parentNodeIndex + 1 === parentNodes.length) {
          parentNodes.push(getBaseParentNode(baseLocationNode, organType, schemaType));
        } else {
          childNodes.push(
            getBaseChildNode(
              baseLocationNode,
              parentNodeInfo.parentId,
              parentNodeInfo.connectionType,
              organType,
              schemaType,
            ),
          );
        }
      }
    });

  return {
    parentNodes,
    childNodes: routeType === 'full' ? childNodes : [],
    segments: schemaSegments,
    legs: schemaLegs,
    unconnectedSegments: routeType === 'full' ? unconnectedSegments : [],
  };
};

type TransformRouteLegsToSchemaParams = {
  segments: RouteSegmentType[];
  legs: Map<string, (AviaLegTypes | GroundLegTypes)[]>;
  aircrafts: BaseLegResourceType<AircraftLegResource>[];
  locations: WithExtendedLocation<RouteLocationType>[];
  organType: OrganType;
  routeType: 'full' | 'main';
  schemaType: 'main' | 'alt';
};

export const transformRouteLegsToSchema = ({
  segments,
  legs,
  aircrafts,
  organType,
  locations,
  routeType = 'full',
  schemaType = 'main',
}: TransformRouteLegsToSchemaParams): {
  nodes: (RouteNodeType<RouteChildNodeDataType> | RouteNodeType)[];
  edges: unknown[];
  blockHeight: number;
  unconnectedLegs: TreeLeg[];
} => {
  const { segments: normalizedSegments, legs: normalizedLegs } = normalizeRouteData(segments, legs);

  const {
    parentNodes,
    childNodes,
    segments: schemaSegments,
    legs: schemaLegs,
    unconnectedSegments,
  } = getNodes({ segments: normalizedSegments, legs: normalizedLegs, locations, organType, routeType, schemaType });
  const {
    edges,
    nodes: updatedNodes,
    blockHeight,
  } = getRouteEdges(schemaSegments, schemaLegs, parentNodes, childNodes, aircrafts, schemaType);

  const unconnectedLegs =
    routeType === 'full'
      ? computeUnconnectedLegs({
          segments: unconnectedSegments,
          legs: schemaLegs,
          aircrafts,
          locations,
          organType,
          schemaType,
        })
      : [];

  return { nodes: updatedNodes, edges, blockHeight, unconnectedLegs };
};
